export interface Task {
  id: string;
  type: 'TODO' | 'FIXME' | 'HACK' | 'NOTE' | 'BUG' | 'OPTIMIZE' | 'REFACTOR';
  text: string;
  file: string;
  line: number;
  column: number;
  priority?: 'low' | 'medium' | 'high';
  assignee?: string;
  date?: string;
  context?: string;
  metadata?: Record<string, any>;
}

export interface TaskPattern {
  pattern: RegExp;
  priority?: 'low' | 'medium' | 'high';
}

export interface ExtractionOptions {
  customPatterns?: Record<string, TaskPattern>;
  includeContext?: boolean;
  contextLines?: number;
  parseMetadata?: boolean;
}

export class TaskExtractor {
  private defaultPatterns: Record<string, TaskPattern> = {
    TODO: { pattern: /TODO[:\s]+(.+)/gi, priority: 'medium' },
    FIXME: { pattern: /FIXME[:\s]+(.+)/gi, priority: 'high' },
    HACK: { pattern: /HACK[:\s]+(.+)/gi, priority: 'low' },
    NOTE: { pattern: /NOTE[:\s]+(.+)/gi, priority: 'low' },
    BUG: { pattern: /BUG[:\s]+(.+)/gi, priority: 'high' },
    OPTIMIZE: { pattern: /OPTIMIZE[:\s]+(.+)/gi, priority: 'medium' },
    REFACTOR: { pattern: /REFACTOR[:\s]+(.+)/gi, priority: 'medium' },
  };

  /**
   * Extract tasks from content
   */
  extract(content: string, filePath: string, options?: ExtractionOptions): Task[] {
    const tasks: Task[] = [];
    const lines = content.split('\n');
    const patterns = options?.customPatterns || this.defaultPatterns;

    lines.forEach((line, lineIndex) => {
      Object.entries(patterns).forEach(([type, config]) => {
        const matches = [...line.matchAll(config.pattern)];
        
        matches.forEach(match => {
          if (match.index !== undefined) {
            const task: Task = {
              id: `${filePath}:${lineIndex + 1}:${match.index}`,
              type: type as Task['type'],
              text: match[1].trim(),
              file: filePath,
              line: lineIndex + 1,
              column: match.index + 1,
              priority: config.priority,
            };

            // Extract metadata if enabled
            if (options?.parseMetadata) {
              const metadata = this.extractMetadata(task.text);
              task.text = metadata.text;
              task.assignee = metadata.assignee;
              task.date = metadata.date;
              task.priority = metadata.priority || task.priority;
              task.metadata = metadata.extra;
            }

            // Add context if requested
            if (options?.includeContext) {
              const contextLines = options.contextLines || 2;
              task.context = this.getContext(lines, lineIndex, contextLines);
            }

            tasks.push(task);
          }
        });
      });
    });

    return tasks;
  }

  /**
   * Extract metadata from task text
   * Format: TODO(assignee): text [priority:high] [due:2024-01-01] {key:value}
   */
  private extractMetadata(text: string): {
    text: string;
    assignee?: string;
    date?: string;
    priority?: 'low' | 'medium' | 'high';
    extra: Record<string, any>;
  } {
    let cleanText = text;
    const metadata: any = { extra: {} };

    // Extract assignee (TODO(john): ...)
    const assigneeMatch = text.match(/^\(([^)]+)\):\s*/);
    if (assigneeMatch) {
      metadata.assignee = assigneeMatch[1];
      cleanText = cleanText.replace(assigneeMatch[0], '');
    }

    // Extract priority [priority:high]
    const priorityMatch = cleanText.match(/\[priority:(low|medium|high)\]/i);
    if (priorityMatch) {
      metadata.priority = priorityMatch[1].toLowerCase() as Task['priority'];
      cleanText = cleanText.replace(priorityMatch[0], '');
    }

    // Extract date [due:2024-01-01]
    const dateMatch = cleanText.match(/\[due:(\d{4}-\d{2}-\d{2})\]/);
    if (dateMatch) {
      metadata.date = dateMatch[1];
      cleanText = cleanText.replace(dateMatch[0], '');
    }

    // Extract custom metadata {key:value}
    const metadataMatches = [...cleanText.matchAll(/\{([^:}]+):([^}]+)\}/g)];
    metadataMatches.forEach(match => {
      metadata.extra[match[1]] = match[2];
      cleanText = cleanText.replace(match[0], '');
    });

    return {
      text: cleanText.trim(),
      ...metadata,
    };
  }

  /**
   * Get context lines around a task
   */
  private getContext(lines: string[], lineIndex: number, contextLines: number): string {
    const start = Math.max(0, lineIndex - contextLines);
    const end = Math.min(lines.length, lineIndex + contextLines + 1);
    
    return lines
      .slice(start, end)
      .map((line, idx) => {
        const actualLine = start + idx;
        const prefix = actualLine === lineIndex ? '>' : ' ';
        return `${prefix} ${actualLine + 1}: ${line}`;
      })
      .join('\n');
  }

  /**
   * Extract tasks from multiple files
   */
  async extractFromFiles(files: Array<{ path: string; content: string }>, options?: ExtractionOptions): Promise<Task[]> {
    const allTasks: Task[] = [];

    for (const file of files) {
      const tasks = this.extract(file.content, file.path, options);
      allTasks.push(...tasks);
    }

    return allTasks;
  }

  /**
   * Group tasks by type
   */
  groupByType(tasks: Task[]): Record<string, Task[]> {
    return tasks.reduce((groups, task) => {
      const type = task.type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(task);
      return groups;
    }, {} as Record<string, Task[]>);
  }

  /**
   * Group tasks by file
   */
  groupByFile(tasks: Task[]): Record<string, Task[]> {
    return tasks.reduce((groups, task) => {
      const file = task.file;
      if (!groups[file]) {
        groups[file] = [];
      }
      groups[file].push(task);
      return groups;
    }, {} as Record<string, Task[]>);
  }

  /**
   * Filter tasks by priority
   */
  filterByPriority(tasks: Task[], priority: 'low' | 'medium' | 'high'): Task[] {
    return tasks.filter(task => task.priority === priority);
  }

  /**
   * Sort tasks by various criteria
   */
  sortTasks(tasks: Task[], by: 'priority' | 'type' | 'file' | 'line'): Task[] {
    const sorted = [...tasks];

    switch (by) {
      case 'priority':
        const priorityOrder = { high: 0, medium: 1, low: 2, undefined: 3 };
        sorted.sort((a, b) => 
          (priorityOrder[a.priority || 'undefined'] || 3) - 
          (priorityOrder[b.priority || 'undefined'] || 3)
        );
        break;
      
      case 'type':
        sorted.sort((a, b) => a.type.localeCompare(b.type));
        break;
      
      case 'file':
        sorted.sort((a, b) => a.file.localeCompare(b.file));
        break;
      
      case 'line':
        sorted.sort((a, b) => {
          const fileCompare = a.file.localeCompare(b.file);
          return fileCompare !== 0 ? fileCompare : a.line - b.line;
        });
        break;
    }

    return sorted;
  }
}